import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import { Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import config from '../../config';

const PackagingMaterialStockDetails = () => {
  const { pmId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStockDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/packing-material-stock/${pmId}/stock-details`);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch PM stock details:', err);
      setError('Failed to load stock details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pmId]);

  useEffect(() => {
    fetchStockDetails();
  }, [fetchStockDetails]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <Spinner animation="border" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert variant="danger">{error}</Alert>
      </Layout>
    );
  }

  if (!data) return null;

  const { packingMaterial, totalAvailableStock, stockByWarehouse, inwardEntries, outwardEntries } = data;

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/operations-dashboard">Operations Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/operations-dashboard">Stock Overview</Link></li>
          <li className="breadcrumb-item active">{packingMaterial.name}</li>
        </ol>
      </nav>

      {/* PM Header */}
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">{packingMaterial.name}</h4>
          <small>{packingMaterial.alias} • {packingMaterial.size}</small>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div><strong>Total Available Stock:</strong></div>
              <div className="h4 text-success">
                {totalAvailableStock.toLocaleString()} 
              </div>
              {totalAvailableStock <= (packingMaterial.reorderQuantity || 0) && (
                <Badge bg="danger" className="me-2">Low Stock</Badge>
              )}
            </Col>
            <Col md={6}>
              <div><strong>Reorder Level:</strong> {packingMaterial.reorderQuantity || 0} </div>
              <div><strong>Description:</strong> {packingMaterial.description || 'N/A'}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Stock by Warehouse */}
      <Card className="mb-4">
        <Card.Header>
          <h5>Stock by Warehouse</h5>
        </Card.Header>
        <Card.Body>
          {stockByWarehouse.length > 0 ? (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th>Available</th>
                  <th>Total In</th>
                  <th>Total Out</th>
                  <th>Last Update</th>
                </tr>
              </thead>
              <tbody>
                {stockByWarehouse.map((stock, index) => (
                  <tr key={index}>
                    <td>{stock.warehouse.label}</td>
                    <td className="fw-bold">{stock.available.toLocaleString()} </td>
                    <td>{stock.totalIn.toLocaleString()}</td>
                    <td>{stock.totalOut.toLocaleString()}</td>
                    <td>
                      {formatDate(stock.lastInwardAt || stock.lastOutwardAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted">No stock data available</div>
          )}
        </Card.Body>
      </Card>

      {/* Inward Entries (Last 10) */}
      <Card className="mb-4">
        <Card.Header>
          <h5>Recent Inward Entries</h5>
          <small className="text-muted">Total: {inwardEntries.length}</small>
        </Card.Header>
        <Card.Body>
          {inwardEntries.length > 0 ? (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Entry #</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Warehouse</th>
                  {/* <th>Supplier</th> */}
                  <th>PO</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {inwardEntries.slice(0, 10).map(entry => (
                  <tr key={entry._id}>
                    <td><Badge bg="info">{entry.entryNumber}</Badge></td>
                    <td>{entry.entryType}</td>
                    <td className="text-success fw-bold">+{entry.quantity.toLocaleString()}</td>
                    {/* <td>{entry.unit}</td> */}
                    <td>{entry.warehouseLocation?.label}</td>
                    {/* <td>{entry.supplier?.business_name || 'N/A'}</td> */}
                    <td>{entry.pmPurchaseOrder?.poNumber || 'N/A'}</td>
                    <td>{formatDate(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted">No inward entries</div>
          )}
        </Card.Body>
      </Card>

      {/* Outward Entries (Last 10) */}
      <Card className="mb-4">
        <Card.Header>
          <h5>Recent Outward Entries</h5>
          <small className="text-muted">Total: {outwardEntries.length}</small>
        </Card.Header>
        <Card.Body>
          {outwardEntries.length > 0 ? (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Entry #</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Warehouse</th>
                  <th>Product/Batch</th>
                  <th>Purpose</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {outwardEntries.slice(0, 10).map(entry => (
                  <tr key={entry._id}>
                    <td><Badge bg="warning">{entry.entryNumber}</Badge></td>
                    <td>{entry.outwardType}</td>
                    <td className="text-danger fw-bold">-{entry.quantity.toLocaleString()}</td>
                    <td>{entry.unit}</td>
                    <td>{entry.warehouseLocation?.name} ({entry.warehouseLocation?.code})</td>
                    <td>
                      {entry.product?.productName ?
                        `${entry.product.productName} (${entry.product.productCode})` :
                        entry.productionBatch?.batchNumber || 'N/A'
                      }
                    </td>
                    <td>{entry.packingOperation?.operationType || entry.dispatchNote || 'Production'}</td>
                    <td>{formatDate(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted">No outward entries</div>
          )}
        </Card.Body>
      </Card>
    </Layout>
  );
};

export default PackagingMaterialStockDetails;
